import {
    Button,
    FormControl,
    FormLabel,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import SportSelect from 'components/SportSelect';
import { useState } from 'react';
import { translate } from '../../../i18n';
import { CoachPricingRuleDto } from '../../../models/coach/dto/coach-pricing-rule.dto';
import { CreateCoachInputDto } from '../../../models/coach/dto/create-coatch.dto';
import { SportType } from '../../../models/court/dto/court.dto';
import CoachPricingEditor from '../../coach-pricing-management/components/CoachPricingEditor';
import { getDefaultCurrency } from '../../court-pricing-management/components/CurrencyPicker';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useCreateCoach } from '../hooks/useCreateCoach';
import { CoachContactDraft, coachContactPayload, EMPTY_COACH_CONTACT_DRAFT } from '../utils/coach-contact';
import CoachContactFields from './CoachContactFields';

const ONE_HOUR_MS = 3600000;

type FormDataType = CreateCoachInputDto & { pricingRules: CoachPricingRuleDto[] };
const defaultValues: FormDataType = {
    firstName: '',
    lastName: '',
    sports: [SportType.PADEL],
    organizationId: '',
    pricingRules: [{ currency: getDefaultCurrency(), hourlyFees: 10, participantCount: 1, id: '', coachId: '', sport: SportType.PADEL, priceDuration: ONE_HOUR_MS }],
};
export default function CreateCoachForm() {
    const { mutateAsync: createCoach, isLoading: isCreating } = useCreateCoach();
    const [formData, setFormData] = useState<FormDataType>(defaultValues);
    // MYS-75 — phone, its public toggle and the booking-notification email.
    // Creating a coach is admin-only (`CoachList` renders this form for org
    // managers only), so all three are offered here.
    const [contact, setContact] = useState<CoachContactDraft>(EMPTY_COACH_CONTACT_DRAFT);
    const contactPayload = coachContactPayload(contact);
    const { selectedOrganization } = useSelectedOrganization();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();


    const closeModal = () => {
        onClose();
        setFormData({ ...defaultValues });
        setContact(EMPTY_COACH_CONTACT_DRAFT);
    };

    const handleInputChange = (key: keyof CreateCoachInputDto, value: any) => {
        setFormData({ ...formData, [key]: value });
    };

    const handleSubmit = async () => {
        if (!contactPayload) return;
        try {
            await createCoach({ ...formData, ...contactPayload, organizationId: selectedOrganization?.id });
            closeModal();
        } catch (error) {
            toast({
                title: translate('error'),
                description: (error as Error)?.message || translate('error'),
                status: 'error',
                duration: 5000,
                position: 'top-right'
            });
        }
    };
    return (
        <>
            <Button colorScheme="brand" onClick={onOpen}>
                {translate('coachs.add_new')}
            </Button>
            <FormModal
                isOpen={isOpen}
                onClose={closeModal}
                title={translate('coachs.add_new')}
                onSubmit={handleSubmit}
                submitLabel={translate('submit')}
                cancelLabel={translate('cancel')}
                isSubmitting={isCreating}
                isSubmitDisabled={!contactPayload}
                size="2xl"
            >
                <FormStack density="section">
                    <FormField
                        type="text"
                        label={translate('coachs.firstName')}
                        value={formData.firstName}
                        onChange={(value) => handleInputChange('firstName', value)}
                    />
                    <FormField
                        type="text"
                        label={translate('coachs.lastName')}
                        value={formData.lastName}
                        onChange={(value) => handleInputChange('lastName', value)}
                    />

                    <CoachContactFields value={contact} onChange={setContact} isDisabled={isCreating} />

                    <FormControl>
                        <FormLabel>{translate('event.sport')}</FormLabel>
                        <SportSelect
                            value={formData.sports || []}
                            onChange={(next) => handleInputChange('sports', next)}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>{translate('coachs.pricing_rules')}</FormLabel>
                        <CoachPricingEditor
                            title=''
                            coach={{ ...formData, id: '', reviewCount: 0 }}
                            pricingRules={formData.pricingRules}
                            setPricingRules={(newValue) => handleInputChange('pricingRules', newValue)} />
                    </FormControl>
                </FormStack>
            </FormModal>
        </>
    )
}
